/**
 * Bot Inspiration Feed Data
 *
 * Story 6.13: Bot Inspiration Feed on Landing Page
 *
 * 100 curated bot idea suggestions across ~15 categories.
 * Each idea uses an i18n key for the display name so it can be translated.
 */

// ============================================================================
// Types
// ============================================================================

export interface BotIdea {
  id: string;
  emoji: string;
  nameKey: string;
  category: string;
}

// ============================================================================
// Fisher-Yates Shuffle
// ============================================================================

/**
 * Shuffle an array in-place using the Fisher-Yates algorithm.
 * Returns a new shuffled array (does not mutate the input).
 */
export function shuffleBotIdeas(ideas: BotIdea[]): BotIdea[] {
  const shuffled = [...ideas];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = shuffled[i]!;
    shuffled[i] = shuffled[j]!;
    shuffled[j] = temp;
  }
  return shuffled;
}

// ============================================================================
// Bot Ideas Data (~100 ideas across 15 categories)
// ============================================================================

export const BOT_IDEAS: BotIdea[] = [
  // Events (7)
  { id: 'events-1', emoji: '🏆', nameKey: 'landing.inspirationFeed.ideas.tournamentFaqBot', category: 'events' },
  { id: 'events-2', emoji: '🎪', nameKey: 'landing.inspirationFeed.ideas.festivalGuideBot', category: 'events' },
  { id: 'events-3', emoji: '🎤', nameKey: 'landing.inspirationFeed.ideas.conferenceHelperBot', category: 'events' },
  { id: 'events-4', emoji: '🏃', nameKey: 'landing.inspirationFeed.ideas.marathonInfoBot', category: 'events' },
  { id: 'events-5', emoji: '🎭', nameKey: 'landing.inspirationFeed.ideas.theaterShowBot', category: 'events' },
  { id: 'events-6', emoji: '🎶', nameKey: 'landing.inspirationFeed.ideas.concertInfoBot', category: 'events' },
  { id: 'events-7', emoji: '📅', nameKey: 'landing.inspirationFeed.ideas.eventScheduleBot', category: 'events' },

  // Food & Restaurant (7)
  { id: 'food-1', emoji: '🍕', nameKey: 'landing.inspirationFeed.ideas.pizzaMenuBot', category: 'food' },
  { id: 'food-2', emoji: '🍣', nameKey: 'landing.inspirationFeed.ideas.sushiOrderBot', category: 'food' },
  { id: 'food-3', emoji: '☕', nameKey: 'landing.inspirationFeed.ideas.cafeMenuBot', category: 'food' },
  { id: 'food-4', emoji: '🍰', nameKey: 'landing.inspirationFeed.ideas.bakeryBot', category: 'food' },
  { id: 'food-5', emoji: '🌮', nameKey: 'landing.inspirationFeed.ideas.foodTruckBot', category: 'food' },
  { id: 'food-6', emoji: '🥗', nameKey: 'landing.inspirationFeed.ideas.nutritionBot', category: 'food' },
  { id: 'food-7', emoji: '🍷', nameKey: 'landing.inspirationFeed.ideas.wineBarBot', category: 'food' },

  // Education (7)
  { id: 'edu-1', emoji: '📚', nameKey: 'landing.inspirationFeed.ideas.courseInfoBot', category: 'education' },
  { id: 'edu-2', emoji: '🎓', nameKey: 'landing.inspirationFeed.ideas.admissionsBot', category: 'education' },
  { id: 'edu-3', emoji: '✏️', nameKey: 'landing.inspirationFeed.ideas.homeworkHelperBot', category: 'education' },
  { id: 'edu-4', emoji: '🧪', nameKey: 'landing.inspirationFeed.ideas.scienceLabBot', category: 'education' },
  { id: 'edu-5', emoji: '📖', nameKey: 'landing.inspirationFeed.ideas.libraryBot', category: 'education' },
  { id: 'edu-6', emoji: '🌍', nameKey: 'landing.inspirationFeed.ideas.languageTutorBot', category: 'education' },
  { id: 'edu-7', emoji: '🧮', nameKey: 'landing.inspirationFeed.ideas.mathTutorBot', category: 'education' },

  // Healthcare (6)
  { id: 'health-1', emoji: '🏥', nameKey: 'landing.inspirationFeed.ideas.clinicFaqBot', category: 'healthcare' },
  { id: 'health-2', emoji: '💊', nameKey: 'landing.inspirationFeed.ideas.pharmacyBot', category: 'healthcare' },
  { id: 'health-3', emoji: '🦷', nameKey: 'landing.inspirationFeed.ideas.dentalClinicBot', category: 'healthcare' },
  { id: 'health-4', emoji: '👁️', nameKey: 'landing.inspirationFeed.ideas.optometryBot', category: 'healthcare' },
  { id: 'health-5', emoji: '🧠', nameKey: 'landing.inspirationFeed.ideas.mentalHealthBot', category: 'healthcare' },
  { id: 'health-6', emoji: '🩺', nameKey: 'landing.inspirationFeed.ideas.doctorOfficeBot', category: 'healthcare' },

  // Fitness (7)
  { id: 'fitness-1', emoji: '💪', nameKey: 'landing.inspirationFeed.ideas.gymInfoBot', category: 'fitness' },
  { id: 'fitness-2', emoji: '🧘', nameKey: 'landing.inspirationFeed.ideas.yogaStudioBot', category: 'fitness' },
  { id: 'fitness-3', emoji: '🏊', nameKey: 'landing.inspirationFeed.ideas.swimClubBot', category: 'fitness' },
  { id: 'fitness-4', emoji: '🚴', nameKey: 'landing.inspirationFeed.ideas.cyclingClubBot', category: 'fitness' },
  { id: 'fitness-5', emoji: '🥊', nameKey: 'landing.inspirationFeed.ideas.boxingGymBot', category: 'fitness' },
  { id: 'fitness-6', emoji: '🏋️', nameKey: 'landing.inspirationFeed.ideas.personalTrainerBot', category: 'fitness' },
  { id: 'fitness-7', emoji: '🧗', nameKey: 'landing.inspirationFeed.ideas.climbingGymBot', category: 'fitness' },

  // Travel (7)
  { id: 'travel-1', emoji: '✈️', nameKey: 'landing.inspirationFeed.ideas.travelAgencyBot', category: 'travel' },
  { id: 'travel-2', emoji: '🏨', nameKey: 'landing.inspirationFeed.ideas.hotelConciergeBot', category: 'travel' },
  { id: 'travel-3', emoji: '🗺️', nameKey: 'landing.inspirationFeed.ideas.tourGuideBot', category: 'travel' },
  { id: 'travel-4', emoji: '🏖️', nameKey: 'landing.inspirationFeed.ideas.beachResortBot', category: 'travel' },
  { id: 'travel-5', emoji: '⛷️', nameKey: 'landing.inspirationFeed.ideas.skiResortBot', category: 'travel' },
  { id: 'travel-6', emoji: '🚢', nameKey: 'landing.inspirationFeed.ideas.cruiseInfoBot', category: 'travel' },
  { id: 'travel-7', emoji: '🏕️', nameKey: 'landing.inspirationFeed.ideas.campingGuideBot', category: 'travel' },

  // Retail & Shopping (7)
  { id: 'retail-1', emoji: '👗', nameKey: 'landing.inspirationFeed.ideas.fashionStoreBot', category: 'retail' },
  { id: 'retail-2', emoji: '👟', nameKey: 'landing.inspirationFeed.ideas.shoeStoreBot', category: 'retail' },
  { id: 'retail-3', emoji: '🛒', nameKey: 'landing.inspirationFeed.ideas.groceryBot', category: 'retail' },
  { id: 'retail-4', emoji: '💎', nameKey: 'landing.inspirationFeed.ideas.jewelryStoreBot', category: 'retail' },
  { id: 'retail-5', emoji: '📱', nameKey: 'landing.inspirationFeed.ideas.techStoreBot', category: 'retail' },
  { id: 'retail-6', emoji: '🎁', nameKey: 'landing.inspirationFeed.ideas.giftShopBot', category: 'retail' },
  { id: 'retail-7', emoji: '🏪', nameKey: 'landing.inspirationFeed.ideas.localShopBot', category: 'retail' },

  // HR & Workplace (7)
  { id: 'hr-1', emoji: '👔', nameKey: 'landing.inspirationFeed.ideas.hrPolicyBot', category: 'hr' },
  { id: 'hr-2', emoji: '🏢', nameKey: 'landing.inspirationFeed.ideas.officeGuideBot', category: 'hr' },
  { id: 'hr-3', emoji: '📋', nameKey: 'landing.inspirationFeed.ideas.onboardingBot', category: 'hr' },
  { id: 'hr-4', emoji: '🤝', nameKey: 'landing.inspirationFeed.ideas.teamBuildingBot', category: 'hr' },
  { id: 'hr-5', emoji: '📊', nameKey: 'landing.inspirationFeed.ideas.benefitsBot', category: 'hr' },
  { id: 'hr-6', emoji: '💼', nameKey: 'landing.inspirationFeed.ideas.recruitmentBot', category: 'hr' },
  { id: 'hr-7', emoji: '🏠', nameKey: 'landing.inspirationFeed.ideas.remoteWorkBot', category: 'hr' },

  // Pets (6)
  { id: 'pets-1', emoji: '🐕', nameKey: 'landing.inspirationFeed.ideas.dogTrainingBot', category: 'pets' },
  { id: 'pets-2', emoji: '🐈', nameKey: 'landing.inspirationFeed.ideas.catCareBot', category: 'pets' },
  { id: 'pets-3', emoji: '🐾', nameKey: 'landing.inspirationFeed.ideas.petStoreBot', category: 'pets' },
  { id: 'pets-4', emoji: '🐠', nameKey: 'landing.inspirationFeed.ideas.aquariumBot', category: 'pets' },
  { id: 'pets-5', emoji: '🐴', nameKey: 'landing.inspirationFeed.ideas.horseStableBot', category: 'pets' },
  { id: 'pets-6', emoji: '🐶', nameKey: 'landing.inspirationFeed.ideas.dogGroomingBot', category: 'pets' },

  // Beauty & Wellness (7)
  { id: 'beauty-1', emoji: '💅', nameKey: 'landing.inspirationFeed.ideas.nailSalonBot', category: 'beauty' },
  { id: 'beauty-2', emoji: '💇', nameKey: 'landing.inspirationFeed.ideas.hairSalonBot', category: 'beauty' },
  { id: 'beauty-3', emoji: '🧖', nameKey: 'landing.inspirationFeed.ideas.spaBot', category: 'beauty' },
  { id: 'beauty-4', emoji: '💄', nameKey: 'landing.inspirationFeed.ideas.makeupStudioBot', category: 'beauty' },
  { id: 'beauty-5', emoji: '🌿', nameKey: 'landing.inspirationFeed.ideas.herbalShopBot', category: 'beauty' },
  { id: 'beauty-6', emoji: '🧴', nameKey: 'landing.inspirationFeed.ideas.skincareBot', category: 'beauty' },
  { id: 'beauty-7', emoji: '💆', nameKey: 'landing.inspirationFeed.ideas.massageBot', category: 'beauty' },

  // Tech Support (6)
  { id: 'tech-1', emoji: '🖥️', nameKey: 'landing.inspirationFeed.ideas.itHelpdeskBot', category: 'tech' },
  { id: 'tech-2', emoji: '🔧', nameKey: 'landing.inspirationFeed.ideas.troubleshootBot', category: 'tech' },
  { id: 'tech-3', emoji: '📶', nameKey: 'landing.inspirationFeed.ideas.wifiSetupBot', category: 'tech' },
  { id: 'tech-4', emoji: '🖨️', nameKey: 'landing.inspirationFeed.ideas.printerHelpBot', category: 'tech' },
  { id: 'tech-5', emoji: '💻', nameKey: 'landing.inspirationFeed.ideas.softwareGuideBot', category: 'tech' },
  { id: 'tech-6', emoji: '🔒', nameKey: 'landing.inspirationFeed.ideas.securityTipsBot', category: 'tech' },

  // Entertainment (7)
  { id: 'ent-1', emoji: '🎬', nameKey: 'landing.inspirationFeed.ideas.movieTheaterBot', category: 'entertainment' },
  { id: 'ent-2', emoji: '🎮', nameKey: 'landing.inspirationFeed.ideas.gamingCafeBot', category: 'entertainment' },
  { id: 'ent-3', emoji: '🎳', nameKey: 'landing.inspirationFeed.ideas.bowlingAlleyBot', category: 'entertainment' },
  { id: 'ent-4', emoji: '🎨', nameKey: 'landing.inspirationFeed.ideas.artGalleryBot', category: 'entertainment' },
  { id: 'ent-5', emoji: '🎸', nameKey: 'landing.inspirationFeed.ideas.musicSchoolBot', category: 'entertainment' },
  { id: 'ent-6', emoji: '🎯', nameKey: 'landing.inspirationFeed.ideas.escapeRoomBot', category: 'entertainment' },
  { id: 'ent-7', emoji: '🎠', nameKey: 'landing.inspirationFeed.ideas.amusementParkBot', category: 'entertainment' },

  // Wedding (6)
  { id: 'wedding-1', emoji: '💒', nameKey: 'landing.inspirationFeed.ideas.weddingPlannerBot', category: 'wedding' },
  { id: 'wedding-2', emoji: '💐', nameKey: 'landing.inspirationFeed.ideas.floristBot', category: 'wedding' },
  { id: 'wedding-3', emoji: '📸', nameKey: 'landing.inspirationFeed.ideas.photographerBot', category: 'wedding' },
  { id: 'wedding-4', emoji: '🎂', nameKey: 'landing.inspirationFeed.ideas.weddingCakeBot', category: 'wedding' },
  { id: 'wedding-5', emoji: '💍', nameKey: 'landing.inspirationFeed.ideas.venueFinderBot', category: 'wedding' },
  { id: 'wedding-6', emoji: '🎵', nameKey: 'landing.inspirationFeed.ideas.weddingDjBot', category: 'wedding' },

  // Sports (6)
  { id: 'sports-1', emoji: '⚽', nameKey: 'landing.inspirationFeed.ideas.soccerClubBot', category: 'sports' },
  { id: 'sports-2', emoji: '🎾', nameKey: 'landing.inspirationFeed.ideas.tennisClubBot', category: 'sports' },
  { id: 'sports-3', emoji: '🏀', nameKey: 'landing.inspirationFeed.ideas.basketballLeagueBot', category: 'sports' },
  { id: 'sports-4', emoji: '⛳', nameKey: 'landing.inspirationFeed.ideas.golfClubBot', category: 'sports' },
  { id: 'sports-5', emoji: '🏐', nameKey: 'landing.inspirationFeed.ideas.volleyballBot', category: 'sports' },
  { id: 'sports-6', emoji: '🎿', nameKey: 'landing.inspirationFeed.ideas.sportsAcademyBot', category: 'sports' },

  // Non-Profit (6)
  { id: 'nonprofit-1', emoji: '🌱', nameKey: 'landing.inspirationFeed.ideas.charityInfoBot', category: 'nonprofit' },
  { id: 'nonprofit-2', emoji: '🤲', nameKey: 'landing.inspirationFeed.ideas.volunteerBot', category: 'nonprofit' },
  { id: 'nonprofit-3', emoji: '🏘️', nameKey: 'landing.inspirationFeed.ideas.communityBot', category: 'nonprofit' },
  { id: 'nonprofit-4', emoji: '🌊', nameKey: 'landing.inspirationFeed.ideas.environmentBot', category: 'nonprofit' },
  { id: 'nonprofit-5', emoji: '❤️', nameKey: 'landing.inspirationFeed.ideas.donationBot', category: 'nonprofit' },
  { id: 'nonprofit-6', emoji: '📢', nameKey: 'landing.inspirationFeed.ideas.awarenessBot', category: 'nonprofit' },
  { id: 'nonprofit-7', emoji: '🎗️', nameKey: 'landing.inspirationFeed.ideas.fundraiserBot', category: 'nonprofit' },
];
